import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, RefreshCw, Send, Mail, Eye, Loader2, Clock, User, FileText, Download
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProposalMailerHistoryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [emails, setEmails] = useState([]);
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPdfs, setLoadingPdfs] = useState(true);
  const [resending, setResending] = useState(null);
  const [downloading, setDownloading] = useState(null);
  
  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/proposal-mailer/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEmails(data.emails || []);
      }
    } catch (e) {
      console.error('Error loading history:', e);
      toast.error('Failed to load history');
    }
    setLoading(false);
  }, []);
  
  const loadPdfs = useCallback(async () => {
    setLoadingPdfs(true);
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/agreement-generator/v2/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPdfs(data.pdfs || []);
      }
    } catch (e) {
      console.error('Error loading PDFs:', e);
    }
    setLoadingPdfs(false);
  }, []);
  
  useEffect(() => {
    loadHistory();
    loadPdfs();
  }, [loadHistory, loadPdfs]);
  
  const handleResend = async (emailId) => {
    setResending(emailId);
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/proposal-mailer/resend/${emailId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Email resent successfully');
        loadHistory();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to resend');
      }
    } catch (e) {
      toast.error('Failed to resend');
    }
    setResending(null);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getOpenStatus = (email) => {
    const count = email.open_count || 0;
    if (count === 0) {
      return <span className="text-slate-400">not yet</span>;
    }
    
    const lastOpen = email.opens?.[email.opens.length - 1]?.timestamp;
    const lastOpenStr = lastOpen ? formatDate(lastOpen) : '';
    
    return (
      <span className="text-emerald-600 font-medium">
        {count}x · last {lastOpenStr}
      </span>
    );
  };
  
  const handleDownloadPdf = async (pdf) => {
    setDownloading(pdf.id);
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/agreement-generator/v2/download/${pdf.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', pdf.filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Failed to download PDF');
      }
    } catch (e) {
      toast.error('Failed to download PDF');
    }
    setDownloading(null);
  };
  
  const getKindLabel = (kind) => {
    switch (kind) {
      case 'recruitment_agreement':
        return 'Recruitment Agreement';
      default:
        return kind;
    }
  };
  
  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/ats/proposal-mailer')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Document History</h1>
              <p className="text-slate-500">Track sent proposals and generated agreements</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={() => { loadHistory(); loadPdfs(); }}
            disabled={loading || loadingPdfs}
            data-testid="refresh-history-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${(loading || loadingPdfs) ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        
        <Tabs defaultValue="sent-emails" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="sent-emails" className="gap-2" data-testid="sent-emails-tab">
              <Mail className="w-4 h-4" />
              Sent Emails
            </TabsTrigger>
            <TabsTrigger value="generated-pdfs" className="gap-2" data-testid="generated-pdfs-tab">
              <FileText className="w-4 h-4" />
              Generated PDFs
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="sent-emails">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Sent Emails
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : emails.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    No proposals sent yet
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Sent</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">By</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">To</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Subject</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Opened</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {emails.map(email => (
                          <tr key={email.id} className="hover:bg-slate-50" data-testid={`history-row-${email.id}`}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2 text-sm text-slate-600">
                                <Clock className="w-4 h-4 text-slate-400" />
                                {formatDate(email.sent_at)}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2 text-sm">
                                <User className="w-4 h-4 text-slate-400" />
                                <span className="text-slate-700">{email.sent_by_name || 'Unknown'}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm">
                                <div className="font-medium text-slate-700">{email.client_name}</div>
                                <div className="text-slate-500">{email.to_email}</div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-slate-700 max-w-xs truncate">
                                {email.subject}
                              </div>
                              {email.resent_from && (
                                <Badge variant="outline" className="text-xs mt-1">Resent</Badge>
                              )}
                              {email.is_bulk && (
                                <Badge variant="secondary" className="text-xs mt-1">Bulk</Badge>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2 text-sm">
                                <Eye className="w-4 h-4 text-slate-400" />
                                {getOpenStatus(email)}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleResend(email.id)}
                                disabled={resending === email.id}
                                data-testid={`resend-${email.id}`}
                              >
                                {resending === email.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Send className="w-4 h-4 mr-1" />
                                    Resend
                                  </>
                                )}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="generated-pdfs">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Generated PDFs
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingPdfs ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : pdfs.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    No PDFs generated yet
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Created</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">By</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Filename</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Kind</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Download</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {pdfs.map(pdf => (
                          <tr key={pdf.id} className="hover:bg-slate-50" data-testid={`pdf-row-${pdf.id}`}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2 text-sm text-slate-600">
                                <Clock className="w-4 h-4 text-slate-400" />
                                {formatDate(pdf.created_at)}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2 text-sm">
                                <User className="w-4 h-4 text-slate-400" />
                                <span className="text-slate-700">{pdf.created_by_name || 'Unknown'}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm">
                                <div className="font-medium text-slate-700">{pdf.filename}</div>
                                {pdf.company_legal_name && (
                                  <div className="text-slate-500 text-xs">{pdf.company_legal_name}</div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant="secondary" className="text-xs">
                                {getKindLabel(pdf.kind)}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadPdf(pdf)}
                                disabled={downloading === pdf.id}
                                data-testid={`download-${pdf.id}`}
                              >
                                {downloading === pdf.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Download className="w-4 h-4 mr-1" />
                                    Download
                                  </>
                                )}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
